import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Tooltip } from "../ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Plus, Edit2, Trash2, Search, Users, Building2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { useColumnVisibility, getDefaultVisibility } from "../../hooks/useColumnVisibility";
import { DataTable } from "../ui/data-table";
import { DepartmentDialog } from "./DepartmentDialog";
import { toast } from "sonner";
import { usePageSize } from "../../hooks/usePageSize";
import {
  GET_DEPARTMENTS_PAGINATED,
  GET_DEPARTMENT_STATISTICS,
  CREATE_DEPARTMENT_MUTATION,
  UPDATE_DEPARTMENT_MUTATION,
  DELETE_DEPARTMENT_MUTATION,
} from "../../graphql/queries";
import { usePermission } from "../../hooks/usePermission";

interface Department {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  positionCount: number;
  employeeCount: number;
  createdAt: string;
  updatedAt: string;
}

interface DepartmentManagementProps {
  currentUserRole?: string;
}

export const DepartmentManagement: React.FC<DepartmentManagementProps> = ({
  currentUserRole,
}) => {
  const canManage = usePermission("departments.manage");
  const { pageSize: defaultPageSize } = usePageSize();
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [deleteConfirmDepartment, setDeleteConfirmDepartment] = useState<Department | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ]);

  // Debounced search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when sorting changes
  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [sorting]);

  const sortBy = sorting[0]?.id;
  const sortOrder = sorting[0]?.desc ? "desc" : "asc";

  const {
    data: departmentsData,
    loading: departmentsLoading,
    refetch: refetchDepartments,
  } = useQuery(GET_DEPARTMENTS_PAGINATED, {
    variables: {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy,
      sortOrder,
      searchTerm: debouncedSearchTerm || undefined,
    },
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error("Error loading departments:", error);
      toast.error("Błąd podczas ładowania działów");
    },
  });

  const { data: statsData } = useQuery(GET_DEPARTMENT_STATISTICS, {
    onError: (error) => {
      console.error("Error loading department statistics:", error);
    },
  });

  const [createDepartment] = useMutation(CREATE_DEPARTMENT_MUTATION);
  const [updateDepartment] = useMutation(UPDATE_DEPARTMENT_MUTATION);
  const [deleteDepartment] = useMutation(DELETE_DEPARTMENT_MUTATION);

  const departments = departmentsData?.departmentsPaginated?.departments || [];
  const totalDepartments = departmentsData?.departmentsPaginated?.total || 0;
  const pageCount = departmentsData?.departmentsPaginated?.totalPages || 0;
  const statistics = statsData?.departmentStatistics || {
    totalDepartments: 0,
    activeDepartments: 0,
    departmentsWithEmployees: 0,
    emptyDepartments: 0,
  };

  const handleSave = async (id: string | null, data: any) => {
    try {
      if (id) {
        await updateDepartment({
          variables: { id, data },
        });
        toast.success("Dział został zaktualizowany");
      } else {
        await createDepartment({
          variables: { data },
        });
        toast.success("Dział został dodany");
      }
      await refetchDepartments();
    } catch (error: any) {
      console.error("Error saving department:", error);
      throw new Error(error.message || "Błąd podczas zapisywania działu");
    }
  };

  const handleAddNew = () => {
    setSelectedDepartment(null);
    setDialogOpen(true);
  };

  const handleEdit = (department: Department) => {
    setSelectedDepartment(department);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedDepartment(null);
  };

  const handleDeleteClick = (department: Department) => {
    setDeleteConfirmDepartment(department);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmDepartment) return;
    try {
      await deleteDepartment({
        variables: { id: deleteConfirmDepartment.id },
      });
      await refetchDepartments();
      toast.success("Dział został usunięty");
    } catch (error: any) {
      console.error("Error deleting department:", error);
      toast.error(error.message || "Błąd podczas usuwania działu");
    } finally {
      setDeleteConfirmDepartment(null);
    }
  };

  const columns: ColumnDef<Department>[] = React.useMemo(() => [
    {
      accessorKey: "name",
      header: "Nazwa działu",
      cell: ({ row }) => (
        <div className="font-medium min-w-[150px]">{row.original.name}</div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "description",
      header: "Opis",
      cell: ({ row }) => (
        <div className="max-w-xs truncate" title={row.original.description}>
          {row.original.description || "-"}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "positionCount",
      header: "Stanowiska",
      cell: ({ row }) => (
        <div className="flex items-center justify-center space-x-1">
          <Building2 className="w-4 h-4 text-gray-400" />
          <span>{row.original.positionCount}</span>
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "employeeCount",
      header: "Pracownicy",
      cell: ({ row }) => (
        <div className="flex items-center justify-center space-x-1">
          <Users className="w-4 h-4 text-gray-400" />
          <span>{row.original.employeeCount}</span>
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? "default" : "destructive"}>
          {row.original.isActive ? "Aktywny" : "Nieaktywny"}
        </Badge>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "createdAt",
      header: "Data utworzenia",
      cell: ({ row }) =>
        new Date(row.original.createdAt).toLocaleDateString("pl-PL"),
      enableSorting: false,
      meta: { defaultHidden: true },
    },
    {
      id: "actions",
      header: "Akcje",
      enableHiding: false,
      cell: ({ row }) => {
        const hasAssignments =
          row.original.positionCount > 0 || row.original.employeeCount > 0;
        return (
          <div className="flex space-x-2">
            {canManage && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(row.original)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                {hasAssignments ? (
                  <Tooltip
                    content="Nie można usunąć działu z przypisanymi stanowiskami lub pracownikami"
                    side="top"
                  >
                    <Button variant="outline" size="sm" disabled={true}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteClick(row.original)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </>
            )}
          </div>
        );
      },
      enableSorting: false,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canManage]);

  const defaults = React.useMemo(() => getDefaultVisibility(columns), [columns]);
  const { columnVisibility, setColumnVisibility, resetToDefaults } = useColumnVisibility('departments', defaults);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-foreground">
            Zarządzanie działami
          </h2>
          <p className="text-muted-foreground mt-2">
            Dodawaj, edytuj i zarządzaj działami firmy
          </p>
        </div>
        {canManage && (
          <Button
            onClick={handleAddNew}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Dodaj dział</span>
          </Button>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Wszystkie działy</p>
                <p className="text-2xl font-bold">
                  {statistics.totalDepartments}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Building2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Aktywne działy</p>
                <p className="text-2xl font-bold">
                  {statistics.activeDepartments}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  Z przypisanymi pracownikami
                </p>
                <p className="text-2xl font-bold">
                  {statistics.departmentsWithEmployees}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Building2 className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Puste działy</p>
                <p className="text-2xl font-bold">
                  {statistics.emptyDepartments}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Lista działów</CardTitle>
              <CardDescription>
                Zarządzaj działami firmy ({totalDepartments} rekordów)
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Search className="w-4 h-4 text-gray-400" />
              <Input
                placeholder="Szukaj działów..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={departments}
            pageCount={pageCount}
            pagination={pagination}
            onPaginationChange={setPagination}
            sorting={sorting}
            onSortingChange={setSorting}
            loading={departmentsLoading}
            manualPagination={true}
            manualSorting={true}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onResetColumnVisibility={resetToDefaults}
          />
        </CardContent>
      </Card>

      <DepartmentDialog
        department={selectedDepartment}
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSave}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteConfirmDepartment}
        onOpenChange={() => setDeleteConfirmDepartment(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Potwierdź usunięcie</DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunąć dział &quot;{deleteConfirmDepartment?.name}&quot;?
              Tej operacji nie można cofnąć.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmDepartment(null)}
            >
              Anuluj
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Usuń
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
